import React, { memo, useRef, useState, useCallback } from "react";
import { View, Pressable, Platform } from "react-native";
import { Feather } from "@expo/vector-icons";
import * as DocumentPicker from "expo-document-picker";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Label } from "@/components/ui/Label";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FileUploadState = "idle" | "dragover" | "uploading" | "success" | "error";

export interface SelectedFile {
  /** Local URI (file:// on native, blob: / data: on web). */
  uri: string;
  /** Original filename. */
  name: string;
  /** MIME type string (e.g. "application/pdf"). */
  type: string;
  /** File size in bytes (may be undefined on some platforms). */
  size?: number;
  /** Pre-formatted size label (e.g. "2.1 MB"). Overrides the `size`-derived label when present. */
  fileSizeLabel?: string;
}

export interface FileUploadProps {
  /** Field label rendered above the drop zone. */
  label?: TranslationKeys;
  /**
   * Called with the selected file details.
   * Trigger your upload logic here and use `uploadState` / `uploadProgress`
   * to reflect the upload to the user.
   */
  onFile: (file: SelectedFile) => void;
  /** External upload state (allows parent to drive the display). */
  uploadState?: FileUploadState;
  /** 0–100 progress percentage shown during `uploading` state. */
  uploadProgress?: number;
  /**
   * Accepted MIME types for the native document picker.
   * On web, passed as the `accept` attribute of the hidden file input.
   * @example ["application/pdf", "image/png", "image/jpeg"]
   */
  accept?: string[];
  /** Maximum file size in megabytes (used for client-side validation). */
  maxSizeMB?: number;
  /** Currently selected / uploaded file displayed in the success state. */
  currentFile?: SelectedFile | null;
  hint?: TranslationKeys;
  /** Validation / server error message. */
  error?: TranslationKeys;
  disabled?: boolean;
  testID?: string;
  /**
   * Called when the user taps "View document" in the success state.
   * Use to open the file URI in a browser / viewer.
   */
  onViewFile?: () => void;
  /**
   * Called when the user taps the trash icon. Use to clear the file selection.
   */
  onRemoveFile?: () => void;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  /**
   * When true, the component renders the file card without any upload controls,
   * edit affordance, or delete button — suitable for review/read-only contexts.
   */
  readOnly?: boolean;
  containerClassName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIconName(type: string): React.ComponentProps<typeof Feather>["name"] {
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "file-text";
  return "file";
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Featured file icon — blue-05 ring background, matches Figma "featured icon" pattern. */
const FeaturedFileIcon = memo(function FeaturedFileIcon({ mimeType }: { mimeType: string }) {
  return (
    <View
      className="size-10 rounded-full bg-blue-5 items-center justify-center shrink-0 border-blue-5"
      style={{ borderWidth: 6 }}
    >
      <Icon as={Feather} name={fileIconName(mimeType)} size={20} className="text-blue-50" />
    </View>
  );
});

/** Green progress bar — Figma: bg-green-50 (#009942), 8 px tall. */
const ProgressBar = memo(function ProgressBar({ progress }: { progress: number }) {
  const pct = Math.max(0, Math.min(100, progress));
  return (
    <View className="flex-1 h-2 rounded bg-stroke-50 overflow-hidden">
      <View className="h-full rounded bg-green-50" style={{ width: `${pct}%` as `${number}%` }} />
    </View>
  );
});

/**
 * File card shown once a file has been picked (success / active state).
 * Matches Figma node 10377:255375 — featured icon + filename + size + "View document" + trash.
 */
const FileCard = memo(function FileCard({
  file,
  onViewTap,
  onRemoveTap,
}: {
  file: SelectedFile;
  onViewTap?: () => void;
  onRemoveTap?: () => void;
}) {
  const { t, isRTL } = useI18n();
  const sizeText = file.fileSizeLabel ?? formatFileSize(file.size);
  return (
    <View className="relative w-full pe-8">
      <View className="flex-row flex-wrap gap-4 items-start w-full">
        <FeaturedFileIcon mimeType={file.type} />

        {/* Text column: name → size → view link */}
        <View className="min-w-0 flex-1 items-start">
          <AppText weight="medium" numberOfLines={2} className="text-base text-dark-text-50">
            {file.name}
          </AppText>
          {!!sizeText && (
            <AppText weight="medium" className="text-base text-supporting-text text-ellipsis">
              {sizeText}
            </AppText>
          )}
          {!!onViewTap && (
            <Pressable onPress={onViewTap} hitSlop={8}>
              <AppText weight="medium" className="text-base text-blue-50">
                {t("fileUpload.viewDocument")}
              </AppText>
            </Pressable>
          )}
        </View>
      </View>

      {/* Red trash button */}
      {!!onRemoveTap && (
        <Pressable onPress={onRemoveTap} hitSlop={12} className="absolute top-0 p-1 end-0">
          <Icon as={Feather} name="trash-2" size={20} className="text-error" />
        </Pressable>
      )}
    </View>
  );
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A drag-and-drop file upload field.
 *
 * Mirrors the Figma "Input Field / Document" variant (node 10377:255354).
 *
 * - **Web:** renders a dashed drop zone with native HTML drag events and a
 *   hidden `<input type="file">` for the click-to-upload flow.
 * - **Native:** opens `expo-document-picker` on press.
 *
 * The component is purely presentational for upload state — pass `uploadState`
 * and `uploadProgress` from the parent after calling your upload API.
 *
 * @example
 * <FileUpload
 *   label={t("vendor.form.document")}
 *   accept={["application/pdf", "image/png", "image/jpeg"]}
 *   maxSizeMB={2}
 *   onFile={handleFileSelected}
 *   uploadState={state}
 *   uploadProgress={progress}
 *   error={fieldError}
 * />
 */
export const FileUpload = memo(function FileUpload({
  label,
  onFile,
  uploadState: externalState,
  uploadProgress = 0,
  accept,
  maxSizeMB,
  currentFile,
  hint,
  error,
  disabled = false,
  testID,
  onViewFile,
  onRemoveFile,
  required,
  readOnly = false,
  containerClassName,
}: FileUploadProps) {
  const { t } = useI18n();
  const [internalDragOver, setInternalDragOver] = useState(false);
  const webInputRef = useRef<HTMLInputElement | null>(null);

  const state: FileUploadState = externalState ?? (internalDragOver ? "dragover" : "idle");

  // ── Native file picker ────────────────────────────────────────────────────

  const handleNativePick = useCallback(async () => {
    if (disabled) return;
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: accept ?? ["application/pdf", "image/png", "image/jpeg", "image/jpg"],
        copyToCacheDirectory: true,
      });
      if (result.canceled) return;
      const asset = result.assets[0];
      if (!asset) return;

      if (maxSizeMB && asset.size && asset.size > maxSizeMB * 1024 * 1024) {
        // Caller should display error via the `error` prop after onFile is called.
        // We still call onFile so the parent can decide.
      }

      onFile({
        uri: asset.uri,
        name: asset.name,
        type: asset.mimeType ?? "application/octet-stream",
        size: asset.size ?? undefined,
      });
    } catch {
      // Picker dismissed — no-op
    }
  }, [disabled, accept, maxSizeMB, onFile]);

  // ── Web file picker ───────────────────────────────────────────────────────

  const handleWebChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      onFile({
        uri: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
        size: file.size,
      });
      // Reset so the same file can be re-selected
      e.target.value = "";
    },
    [onFile]
  );

  const triggerWebInput = useCallback(() => {
    if (!disabled) webInputRef.current?.click();
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setInternalDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setInternalDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setInternalDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      onFile({
        uri: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
        size: file.size,
      });
    },
    [onFile]
  );

  // ── Derived styles ────────────────────────────────────────────────────────

  const hasFile = currentFile != null;

  const borderClass = error
    ? "border-error"
    : state === "dragover"
      ? "border-blue-50"
      : "border-stroke-50";

  // White bg when a file is present (uploading or success); tinted when idle.
  const bgClass = state === "dragover" ? "bg-blue-5" : "bg-stroke-15";

  // Dashed border only in the idle / drag-over state (no file selected).
  const dashedClass =
    !hasFile && state !== "uploading" && state !== "success" ? "border-dashed" : "";

  const isInteractive = !disabled && state !== "uploading";
  // In error state the user can re-tap to try a different file, so treat it
  // the same as "no file yet" for the click / press handler.
  const canTriggerPick = isInteractive && (!hasFile || state === "error");

  // ── Render ────────────────────────────────────────────────────────────────

  const dropZoneContent = (
    <>
      {state === "uploading" && currentFile ? (
        /*
         * Uploading — Figma node 10377:255384 "Editing"
         * Featured icon + filename + size + green progress bar + % + green checkmark badge
         */
        <View className="relative w-full pr-8">
          <View className="flex-row gap-4 items-start w-full">
            <FeaturedFileIcon mimeType={currentFile.type} />

            <View className="flex-1 items-start min-w-0 gap-0.5">
              <AppText weight="medium" numberOfLines={1} className="text-base text-dark-text-50">
                {currentFile.name}
              </AppText>
              {!!formatFileSize(currentFile.size) && (
                <AppText weight="medium" className="text-base text-supporting-text">
                  {formatFileSize(currentFile.size)}
                </AppText>
              )}
              {/* Progress row */}
              <View className="flex-row items-center gap-3 mt-1">
                <ProgressBar progress={uploadProgress} />
                <AppText
                  weight="medium"
                  className="text-base text-supporting-text"
                  style={{ minWidth: 40, textAlign: "right" }}
                >
                  {uploadProgress}%
                </AppText>
              </View>
            </View>
          </View>

          {/* Green checkmark badge — top-right corner */}
          <View className="absolute top-0 end-0 size-4 rounded-full bg-green-50 items-center justify-center">
            <Icon as={Feather} name="check" size={10} color="white" />
          </View>
        </View>
      ) : (state === "success" || state === "error" || (state === "idle" && hasFile)) &&
        currentFile ? (
        /*
         * File picked — Figma node 10377:255375 "Active"
         * Featured icon + filename + size + "View document" + trash
         */
        <FileCard file={currentFile} onViewTap={onViewFile} onRemoveTap={onRemoveFile} />
      ) : (
        /* Idle / drag-over */
        <>
          <View className="size-10 rounded-full bg-stroke-50 items-center justify-center mb-2">
            <Icon as={Feather} name="upload-cloud" size={20} className="text-supporting-text" />
          </View>
          <View className="flex-row flex-wrap justify-center gap-1 mb-1">
            <AppText weight="medium" className="text-base text-blue-50">
              {t("fileUpload.clickToUpload")}
            </AppText>
            <AppText weight="medium" className="text-base text-supporting-text">
              {t("fileUpload.andAttachFiles")}
            </AppText>
          </View>
          <AppText className="text-sm text-supporting-text text-center tracking-[0.14px]">
            {maxSizeMB != null
              ? t("fileUpload.acceptedFormats", { maxSize: maxSizeMB })
              : t("fileUpload.acceptedFormatsNoSize")}
          </AppText>
        </>
      )}
    </>
  );

  // ── readOnly: render just the file card, no upload affordance ────────────

  if (readOnly && currentFile) {
    return (
      <View className="w-full items-start" testID={testID}>
        {!!label && <Label label={label} required={required} />}
        <View
          className={cn(
            "w-full bg-white border border-stroke-50 rounded-xl p-4",
            containerClassName
          )}
        >
          <FileCard file={currentFile} onViewTap={onViewFile} />
        </View>
      </View>
    );
  }

  if (Platform.OS === "web") {
    return (
      <View className="mb-4 w-full items-start" testID={testID}>
        {!!label && <Label label={label} required={required} />}

        {/* Hidden file input */}
        {/* @ts-ignore — HTMLInputElement ref is web-only */}
        <input
          ref={webInputRef}
          type="file"
          style={{ display: "none" }}
          accept={accept?.join(",") ?? "application/pdf,image/png,image/jpeg"}
          onChange={handleWebChange as unknown as React.ChangeEventHandler<HTMLInputElement>}
        />

        {/* Drop zone */}
        <div
          onClick={canTriggerPick ? triggerWebInput : undefined}
          onDragOver={
            isInteractive
              ? (handleDragOver as unknown as React.DragEventHandler<HTMLDivElement>)
              : undefined
          }
          onDragLeave={
            isInteractive
              ? (handleDragLeave as unknown as React.DragEventHandler<HTMLDivElement>)
              : undefined
          }
          onDrop={
            isInteractive
              ? (handleDrop as unknown as React.DragEventHandler<HTMLDivElement>)
              : undefined
          }
          style={{ cursor: canTriggerPick ? "pointer" : "default", opacity: disabled ? 0.5 : 1 }}
          className={cn(
            "flex flex-col items-center justify-center w-full border rounded-md px-4 py-4 tablet:py-6",
            dashedClass,
            bgClass,
            borderClass,
            containerClassName
          )}
        >
          {dropZoneContent}
        </div>

        {!!hint && !error && (
          <AppText className="text-xs text-dark-text-10 mt-1 text-start">{t(hint)}</AppText>
        )}
        {!!error && (
          <AppText role="alert" className="text-xs text-error mt-1 text-start">
            {t(error)}
          </AppText>
        )}
      </View>
    );
  }

  // ── Native ────────────────────────────────────────────────────────────────

  return (
    <View className="mb-4 w-full items-start" testID={testID}>
      {!!label && <Label label={label} required={required} />}

      <Pressable
        onPress={canTriggerPick ? handleNativePick : undefined}
        disabled={!canTriggerPick}
        accessibilityRole="button"
        accessibilityLabel={label ? t(label) : t("fileUpload.a11y.uploadFile")}
        className={cn(
          "w-full flex-col items-center justify-center border rounded-md px-4 py-4 tablet:py-6",
          dashedClass,
          bgClass,
          borderClass,
          disabled ? "opacity-50" : "",
          containerClassName
        )}
        style={({ pressed }) => [{ opacity: pressed && isInteractive && !hasFile ? 0.7 : 1 }]}
      >
        {dropZoneContent}
      </Pressable>

      {!!hint && !error && (
        <AppText className="text-xs text-dark-text-10 mt-1 text-start">{t(hint)}</AppText>
      )}
      {!!error && (
        <AppText role="alert" className="text-xs text-error mt-1 text-start">
          {t(error)}
        </AppText>
      )}
    </View>
  );
});
